<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :feedbackOperationData="feedbackOperationData"
    :scrollValue="scrollValue"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :customBreadcrumbs="customBreadcrumbs"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('transfers.periodic.edit.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, editTransferHandler)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <component
        :is="getActiveStepComponent"
        :editFormData="editFormData"
        :scheduleTypesList="scheduleTypesList"
        :responseData="responseData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @updateData="updateFormData($event)"
        @formErrorsCleared="clearFormErrors = false"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, requestBeneficiaryName, requestScheduleTypes, editRecurringTransfer, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_PERIODIC)
const { getAccountsBalances, getBeneficiaryName, getScheduleTypes } = mapGetters(moduleMapper.TRANSFERS_PERIODIC)

/* props received
@selectedTransfer { Object } - data received from transfer periodic consult
*/
const props = defineProps({
    selectedTransfer: Object
})

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const scheduleTypesList = ref(null)
const applyErrorState = ref(false)

const customBreadcrumbs = {
    currentArea: t('transfers.periodic.edit.breadcrumbs.currentArea'),
    parentMenus: [{
        id: 1,
        text: t('transfers.periodic.edit.breadcrumbs.firstParent')
    }, {
        id: 2,
        text: t('transfers.periodic.edit.breadcrumbs.secondParent'),
        internalClick: true
    }]
}

/*************************************************
*               Steps/Footer Data                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('transfers.periodic.edit.stepperParams.data') },
    { description: t('transfers.periodic.edit.stepperParams.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/transfers/periodic/edit/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const editFormData = ref({
    originAccountNumber: null,
    originAccountId: null,
    destinationAccountNumber: null,
    destinationAccountId: null,
    beneficiaryName: null,
    amount: null,
    currency: null,
    frequencyCode: null,
    shortCode: null,
    frequency: null,
    frequencyDescription: null,
    expiryDate: null,
    hasUserDefinedEndDate: null,
    transferId: null,
    descriptionDebit: null,
    descriptionCredit: null
})

const mandatoryList = ref([
    'amount',
    'shortCode'
])

const updateFormData = ({ field, inputValue }) => {
    if (field === 'shortCode') {
        const targetFrequency = getScheduleTypes.value.find(type => type.shortCode === inputValue)
        editFormData.value.frequencyCode = targetFrequency.code
        editFormData.value.frequencyDescription = targetFrequency.description
        editFormData.value.frequency = props.selectedTransfer.frequency.toString()
    }
    editFormData.value[field] = inputValue
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = async () => {
	showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false

    const beneficiaryNamePayload = {
        transferType: 'INTRABANK',
        genericDestination: props.selectedTransfer.destinationAccount
    }

	try {
        await requestAccountsBalances()
        await requestScheduleTypes()
        await requestBeneficiaryName(beneficiaryNamePayload)
        setInitialValues()
        scheduleTypesList.value = getScheduleTypes.value
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error, initialRequests)
	}
}

const editTransferHandler = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    const payload = {
		data: {
            amount: editFormData.value.amount,
            currency: editFormData.value.currency,
            descriptionCredit: editFormData.value.descriptionCredit,
            descriptionDebit: editFormData.value.descriptionDebit,
            endDate: editFormData.value.hasUserDefinedEndDate ? editFormData.value.expiryDate : null,
            frequency: {
                frequencyCode: editFormData.value.frequencyCode,
                frequency: editFormData.value.frequencyCode === 'VARIAVEL' ? editFormData.value.frequency : null
            }
        },
        transferId: editFormData.value.transferId,
        accountId: editFormData.value.originAccountId
    }

    try {
        await editRecurringTransfer(payload)
        showOverlayLoader.value = false
    } catch (error) {
        let functionToUse
        const endDateError = error.response.data.code === 'end_date_for_recurring_transfer_is_not_a_working_day'

        endDateError ? functionToUse = endDateNotWorkingDayHandler : functionToUse = editTransferHandler

		errorHandler(error.response.data, functionToUse)
	}
}

const setInitialValues = () => {
    const originAccount = getAccountsBalances.value.find(account => account.accountNumber === props.selectedTransfer.originAccount)
    const targetFrequency = getScheduleTypes.value.find(type => type.shortCode === props.selectedTransfer.frequencyCode)
    editFormData.value.originAccountNumber = props.selectedTransfer.originAccount
    editFormData.value.originAccountId = originAccount.id
    editFormData.value.destinationAccountNumber = props.selectedTransfer.destinationAccount
    editFormData.value.destinationAccountId = props.selectedTransfer.accountId
    editFormData.value.beneficiaryName = getBeneficiaryName.value
    editFormData.value.amount = props.selectedTransfer.amount
    editFormData.value.currency = props.selectedTransfer.currency
    editFormData.value.frequencyCode = targetFrequency.code
    editFormData.value.shortCode = props.selectedTransfer.frequencyCode
    editFormData.value.frequencyDescription = targetFrequency.description
    editFormData.value.frequency = props.selectedTransfer.frequency.toString()
    editFormData.value.expiryDate = props.selectedTransfer.expiryDate
    editFormData.value.hasUserDefinedEndDate = props.selectedTransfer.hasUserDefinedEndDate
    editFormData.value.transferId = props.selectedTransfer.transferId
    editFormData.value.descriptionDebit = props.selectedTransfer.descriptionDebit
    editFormData.value.descriptionCredit = props.selectedTransfer.descriptionCredit
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const feedbackOperationData = ref(null)
const responseData = ref(null)

const successHandler = (data) => {
    const operationId = data.data.transfer ? data.data.transfer.operationId : null
    responseData.value = data

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: operationId } }),
            operationId: operationId
        }
    }
    goToStep(3)
}

const endDateNotWorkingDayHandler = () => {
    goToStep(1)
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*              Footer Buttons Data               *
*                                                *
*************************************************/
const validateInputs = () => {
    const invalidFrequency = editFormData.value.frequencyCode === 'VARIAVEL' && parseInt(editFormData.value.frequency) === 0

    editFormData.value.frequencyCode === 'VARIAVEL' ? mandatoryList.value.push('frequency') : mandatoryList.value = mandatoryList.value.filter(field => field !== 'frequency')

    atLeastOneIsEmpty(editFormData.value, mandatoryList.value) || (!!editFormData.value.amount && editFormData.value.amount <= 0) || invalidFrequency ? showMandatoryErrors.value = true : activeStep.value = 2
}

const clearInputs = () => {
    clearFormErrors.value = true
    setInitialValues()
}

const footerButtonsParams = computed(() => {
    return [{
        label: t('transfers.periodic.edit.footerButtonsParams.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('transfers.periodic.edit.footerButtonsParams.continue'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }, {
        label: t('transfers.periodic.edit.footerButtonsParams.goBack'),
        icon: 'arrow-left-full',
        callbackFunction: () => goToStep(1),
        step: 2
    },
    {
        label: t('transfers.periodic.edit.footerButtonsParams.validate'),
        callbackFunction: editTransferHandler,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 2
    }]
})

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})

</script>

<style lang="scss" scoped>
</style>
